<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close', 'createAssistant']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const assistants = useMapGetter('polarisAssistants/getRecords');

const currentAssistantId = computed(() => route.params.assistantId);

const isAssistantActive = assistant => {
  return assistant.id === Number(currentAssistantId.value);
};

// Badge Live/Setup (spec §4.0): Live quando o assistente tem inbox ligada (`inboxes_count` do payload).
const badgeOf = assistant => {
  const live = (assistant.inboxes_count ?? 0) > 0;
  return {
    tone: live ? 'live' : 'setup',
    label: t(
      live
        ? 'POLARIS.ASSISTANT_SWITCHER.BADGE_LIVE'
        : 'POLARIS.ASSISTANT_SWITCHER.BADGE_SETUP'
    ),
  };
};

const fetchDataForRoute = async (routeName, assistantId) => {
  const dataFetchMap = {
    polaris_assistants_responses_index: async () => {
      await store.dispatch('polarisResponses/get', { assistantId });
      await store.dispatch('polarisResponses/fetchPendingCount', assistantId);
    },
    polaris_assistants_responses_pending: async () => {
      await store.dispatch('polarisResponses/get', {
        assistantId,
        status: 'pending',
      });
    },
    polaris_assistants_documents_index: async () => {
      await store.dispatch('polarisDocuments/get', { assistantId });
    },
    polaris_assistants_scenarios_index: async () => {
      await store.dispatch('polarisScenarios/get', { assistantId });
    },
    polaris_assistants_playground_index: () => {
      // Playground doesn't need pre-fetching, it loads on interaction
    },
    polaris_assistants_inboxes_index: async () => {
      await store.dispatch('polarisInboxes/get', { assistantId });
    },
    polaris_tools_index: async () => {
      await store.dispatch('polarisCustomTools/get', { page: 1 });
    },
    polaris_assistants_settings_index: async () => {
      await store.dispatch('polarisAssistants/show', assistantId);
    },
  };

  const fetchFn = dataFetchMap[routeName];
  if (fetchFn) {
    await fetchFn();
  }
};

const handleAssistantChange = async assistant => {
  if (isAssistantActive(assistant)) return;

  const currentRouteName = route.name;
  const targetRouteName =
    currentRouteName || 'polaris_assistants_get_started_index';

  await fetchDataForRoute(targetRouteName, assistant.id);

  await router.push({
    name: targetRouteName,
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });

  emit('close');
};

// Galeria de modelos (Fase 2): a tela 00, com os papéis e o "Assistente em branco".
const openTemplates = () => {
  router.push({
    name: 'polaris_templates_index',
    params: { accountId: route.params.accountId },
  });
  emit('close');
};

const openCreateAssistantDialog = () => {
  emit('createAssistant');
  emit('close');
};
</script>

<template>
  <div
    class="pt-5 bg-n-alpha-3 backdrop-blur-[100px] outline outline-n-container outline-1 z-50 absolute w-[27.5rem] max-h-96 rounded-xl shadow-md flex flex-col gap-4"
  >
    <div
      class="flex items-center justify-between gap-4 px-6 pb-3 border-b border-n-alpha-2"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2
            class="text-base font-medium cursor-pointer text-n-slate-12 w-fit hover:underline"
          >
            {{ t('POLARIS.ASSISTANT_SWITCHER.ASSISTANTS') }}
          </h2>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('POLARIS.ASSISTANT_SWITCHER.SWITCH_ASSISTANT') }}
        </p>
      </div>
    </div>
    <div
      v-if="assistants.length > 0"
      class="flex flex-col flex-1 min-h-0 gap-2 px-4 pb-1 overflow-y-auto overscroll-contain"
    >
      <Button
        v-for="assistant in assistants"
        :key="assistant.id"
        :label="assistant.name"
        variant="ghost"
        color="slate"
        trailing-icon
        :icon="isAssistantActive(assistant) ? 'i-lucide-check' : ''"
        class="!justify-end !px-2 !py-2 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10 h-9"
        size="sm"
        @click="handleAssistantChange(assistant)"
      >
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ assistant.name || '' }}
        </span>
        <span
          class="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
          :class="
            badgeOf(assistant).tone === 'live'
              ? 'bg-polaris-success text-polaris-success-foreground'
              : 'bg-polaris-warning text-polaris-warning-foreground'
          "
        >
          {{ badgeOf(assistant).label }}
        </span>
        <Avatar
          v-if="assistant"
          :name="assistant.name"
          :size="20"
          icon-name="i-lucide-bot"
          rounded-full
        />
      </Button>
    </div>
    <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
      <p class="text-sm text-n-slate-11">
        {{ t('POLARIS.ASSISTANT_SWITCHER.EMPTY_LIST') }}
      </p>
    </div>
    <div class="flex flex-col gap-1 px-4 pt-2 pb-4 border-t border-n-alpha-2">
      <Button
        :label="t('POLARIS.ASSISTANT_SWITCHER.NEW_FROM_TEMPLATE')"
        variant="ghost"
        color="slate"
        icon="i-lucide-layout-template"
        size="sm"
        class="!justify-start !px-2 hover:!bg-n-alpha-2"
        @click="openTemplates"
      />
      <Button
        :label="t('POLARIS.ASSISTANT_SWITCHER.BLANK_ASSISTANT')"
        variant="ghost"
        color="slate"
        icon="i-lucide-plus"
        size="sm"
        class="!justify-start !px-2 hover:!bg-n-alpha-2"
        @click="openCreateAssistantDialog"
      />
    </div>
  </div>
</template>
